if (!customElements.get('store-selector')) {
  customElements.define(
    'store-selector',
    class StoreSelector extends window.BAO.CustomElement() {
      constructor () {
        super()

        this.state = {
          storefront: this.storefront,
          redirectTarget: undefined,
          customerCountryCode: undefined,
          customerContinent: undefined,
          customerCurrencyCode: undefined,
          showModal: false,
          shippingConflictResolved: false,
          usRedirect: false,
          deRedirect: false,
          euCountries: this.arrayFromString(this.dataset.euCountries),
          usCountries: this.arrayFromString(this.dataset.usCountries),
          gccCountries: this.arrayFromString(this.dataset.gccCountries),
        }

        this.resolveFnc = null

        this.setupListeners()
      }

      arrayFromString (input = '', delimiter = ',') {
        if (typeof input !== 'string') {
          return []
        }

        return input.split(delimiter).map((item) => item.trim())
      }

      parseLanguages (input = '') {
        return this.arrayFromString(input)
          .filter((item) => item.length > 0)
          .map((item) => {
            /*
              Item will be something like: en|English or ar|العربية
            */

            const [code, label] = item.split('|').map((part) => part.trim())

            return { code: code.toLowerCase(), label: label || code }
          })
          .filter((language) => language.code.length > 0)
      }

      pushStoreSelectorEvent (eventName, extra = {}) {
        window.dataLayer = window.dataLayer || []

        window.dataLayer.push({
          event: eventName,
          customer_country: this.state.customerCountryCode,
          ...extra,
        })
      }

      async connectedCallback () {
        if (!this.forceDisplay) {
          if (localStorage.getItem('StoreRedirectModalClosed') === 'true') {
            return
          }

          if (window.Shopify.designMode) return
        }

        const hasStoredPreference =
          localStorage.getItem('StoreRedirectModalClosed') === 'true'

        await this.setGeoInformation()

        if (this.forceDisplay) {
          this.showModal = true
          return
        }

        if (this.state.customerCountryCode === undefined) return

        this.setRedirectTarget()

        const needsRedirect =
          this.state.redirectTarget !== null &&
          this.state.redirectTarget !== this.state.storefront

        if (!needsRedirect) return

        if (hasStoredPreference) {
          this.trackMarketPreferenceConflict()
          return
        }

        this.pushStoreSelectorEvent('store_selector_modal_shown', {
          target_value: this.state.redirectTarget,
        })

        this.showModal = true
      }

      trackMarketPreferenceConflict () {
        if (sessionStorage.getItem(this.marketConflictTrackedKey) === 'true') {
          return
        }

        sessionStorage.setItem(this.marketConflictTrackedKey, 'true')

        this.pushStoreSelectorEvent('market_preference_conflict')
      }

      async setGeoInformation () {
        try {
          const suggestions = await fetch(
            `${window.Shopify.routes.root}browsing_context_suggestions.json` +
              '?country[enabled]=true' +
              '&language[enabled]=true',
          ).then((r) => r.json())

          if (!suggestions?.suggestions?.length) return

          this.state.customerCountry = suggestions.detected_values.country.name
          this.state.customerCountryCode =
            suggestions.detected_values.country.handle

          if (
            this.state.customerCountryCode === 'AU' ||
            this.state.customerCountryCode === 'NZ'
          ) {
            this.state.customerContinent = 'Oceania'
          }

          this.state.customerCurrencyCode = window.Shopify.currency?.active
        } catch (error) {
          console.error('Error fetching browsing context suggestions:', error)
        }
      }

      isUSCustomer (code) {
        return this.state.usCountries.includes(code)
      }

      isEUCustomer (code) {
        return this.state.euCountries.includes(code)
      }

      isGCCCustomer (code) {
        return this.state.gccCountries.includes(code)
      }

      setRedirectTarget () {
        /*

          If you make any changes to the logic below, please make sure to test it on the live sites following this test sheet

          The modal will appear under the following conditions.

          US: If the customer country is not US or CAD
          UK: If the customer is in AU, NZ, US or CAD
          AU: If the customer is not in AU, NZ
          DE: If the customer is in DE or AT, they will be sent to the DE storefront
          EU: If the customer is in GB, they will be sent to the UK storefront

          Customers will be offered a select dropdown to pick a store, with the most appropriate store pre-selected

          Customers in US and CAD will be offered the US storefront
          Customers in AU and NZ will be offered the AU storefront

          Customers in AE, KW, SA, OM, QA will be offered the AE storefront

          Finally, customers in all other countries will be offered the UK storefront.

          If a customer is detected as DE and they select EUR, they will be redirected to the DE store

          Update the above test sheet if the business requirements have changed

        */

        // DE and AT belong on the DE store. Returning here also covers
        // visitors already on it, so the modal does not open.
        if (
          this.state.customerCountryCode === 'DE' ||
          this.state.customerCountryCode === 'AT'
        ) {
          this.state.redirectTarget = 'DE'
          this.state.deRedirect = this.state.storefront !== 'DE'
          return
        }

        // GCC Customer Check — any GCC-list country not already on the AE market

        if (
          this.isGCCCustomer(this.state.customerCountryCode) &&
          this.state.storefront !== 'AE'
        ) {
          this.state.redirectTarget = 'AE'
          return
        }

        // EU Customer Check — any EU-list country not already on the EU market

        if (this.isEUCustomer(this.state.customerCountryCode)) {
          this.state.redirectTarget = 'EU'
          return
        }

        // If storefront is US

        if (this.state.storefront === 'US') {
          if (this.isUSCustomer(this.state.customerCountryCode)) {
            this.state.redirectTarget = 'US'
          } else {
            if (this.state.customerContinent !== 'Oceania') {
              this.state.redirectTarget = 'UK'
              return
            }

            if (this.state.customerContinent === 'Oceania') {
              this.state.redirectTarget = 'AU'
            }
          }
        }

        // If storefront is UK

        if (this.state.storefront === 'UK') {
          this.state.redirectTarget = 'UK'

          if (this.isUSCustomer(this.state.customerCountryCode)) {
            this.state.redirectTarget = 'US'
            this.state.usRedirect = true
            return
          }

          if (this.state.customerContinent === 'Oceania') {
            this.state.redirectTarget = 'AU'
          }
        }

        // If storefront is AU

        if (this.state.storefront === 'AU') {
          this.state.redirectTarget = 'AU'

          if (this.isUSCustomer(this.state.customerCountryCode)) {
            this.state.redirectTarget = 'US'
            this.state.usRedirect = true

            return
          }

          if (this.state.customerContinent !== 'Oceania') {
            this.state.redirectTarget = 'UK'
          }
        }

        // If storefront is EU

        if (this.state.storefront === 'EU') {
          this.state.redirectTarget = 'EU'

          if (this.isUSCustomer(this.state.customerCountryCode)) {
            this.state.redirectTarget = 'US'
            this.state.usRedirect = true
            return
          }

          if (this.state.customerCountryCode === 'GB') {
            this.state.redirectTarget = 'UK'
            return
          }

          if (this.state.customerContinent === 'Oceania') {
            this.state.redirectTarget = 'AU'
          }
        }

        // If storefront is AE

        if (this.state.storefront === 'AE') {
          this.state.redirectTarget = 'AE'

          if (this.isGCCCustomer(this.state.customerCountryCode)) {
            return
          }

          if (this.isUSCustomer(this.state.customerCountryCode)) {
            this.state.redirectTarget = 'US'
            this.state.usRedirect = true
            return
          }

          if (this.state.customerContinent === 'Oceania') {
            this.state.redirectTarget = 'AU'
            return
          }

          this.state.redirectTarget = 'UK'
        }
      }

      updateCurrency (el) {
        const { currencies } = el.dataset

        try {
          if (currencies) {
            const useCustomerCurrencyCode = this.arrayFromString(
              currencies,
            ).includes(this.state.customerCurrencyCode)

            if (useCustomerCurrencyCode) {
              const url = new URL(el.value)
              url.searchParams.set('currency', this.state.customerCurrencyCode)
              el.value = url
            }
          }
        } catch (err) {
          console.error(
            'unable to set customer currency to store url',
            err.message,
          )
        }
      }

      updateLanguages () {
        const field = this.els.languageField?.element
        const select = this.els.language?.element

        if (!field || !select) return

        const option = this.els.select.element.selectedOptions[0]
        const languages = this.parseLanguages(option?.dataset.languages)

        select.innerHTML = ''

        if (languages.length < 2) {
          field.setAttribute('aria-hidden', 'true')
          return
        }

        languages.forEach(({ code, label }, index) => {
          const languageOption = document.createElement('option')

          languageOption.value = index === 0 ? '' : code
          languageOption.textContent = label

          select.appendChild(languageOption)
        })

        field.setAttribute('aria-hidden', 'false')
      }

      applyLanguage (url) {
        const field = this.els.languageField?.element
        const select = this.els.language?.element

        if (!field || !select) return url
        if (field.getAttribute('aria-hidden') !== 'false') return url
        if (!select.value) return url

        try {
          const languageUrl = new URL(url)
          languageUrl.pathname = `/${select.value}${languageUrl.pathname}`

          return languageUrl.toString()
        } catch (err) {
          console.error('unable to set language on store url', err.message)

          return url
        }
      }

      updateView () {
        if (this.state.showModal) {
          let store = 'EU'
          let name = 'Europe'

          switch (this.state.customerCountryCode) {
            case 'US':
              store = 'US'
              name = 'US'
              break

            case 'CA':
              store = 'CA'
              name = 'Canada'
              break

            case 'AU':
            case 'NZ':
              store = 'AU'
              name = 'Australia'
              break

            case 'MX':
              store = 'MX'
              name = 'Mexico'
              break

            case 'PR':
              store = 'US'
              name = 'US'
              break

            case 'GB':
              store = 'UK'
              name = 'UK'
              break

            case 'IL':
              store = 'IL'
              name = 'Israel'
              break

            case 'DE':
            case 'AT':
              store = 'DE'
              name = 'German'
              break

            case 'AE':
            case 'KW':
            case 'SA':
            case 'OM':
            case 'QA':
              store = 'AE'
              name = 'UAE'
              break
          }

          // Update the message content with country and storefront
          this.els.text.elements.forEach((el) => {
            el.innerHTML = this.replacePlaceholders(el.innerHTML, name)
          })

          const select = this.els.select.element
          const options = this.els.option.elements
          const optionMatches = (el, code) =>
            this.arrayFromString(el.dataset.countryCode).some(
              (countryCode) =>
                countryCode.toUpperCase() === String(code).toUpperCase(),
            )

          const selectedOption = [
            this.state.customerCountryCode,
            this.state.redirectTarget,
            store,
          ]
            .filter(Boolean)
            .reduce(
              (match, code) =>
                match || options.find((el) => optionMatches(el, code)),
              undefined,
            )

          if (selectedOption) {
            this.updateCurrency(selectedOption)

            // The selected attribute does not move the live selection, so
            // confirm would submit the first option (UK + ROW).
            const optionIndex = Array.prototype.indexOf.call(
              select.options,
              selectedOption,
            )

            if (optionIndex >= 0) {
              select.selectedIndex = optionIndex
            } else {
              selectedOption.selected = true
            }
          } else {
            console.warn(
              `Store selector: no option configured for ${this.state.customerCountryCode} (${store})`,
            )
          }

          this.updateLanguages()

          window.drawers.activeDrawerKey = 'store-selector'
        }

        if (!this.state.showModal) {
          window.drawers.activeDrawerKey = ''
        }
      }

      replacePlaceholders (text = '', storeName = '') {
        return text
          .replace(/\[land\]/gi, this.state.customerCountry || '')
          .replace(/\[country\]/gi, this.state.customerCountry || '')
          .replace(/\[storefront\]/gi, storeName)
          .replace(/\[country_code\]/gi, this.state.customerCountryCode || '')
      }

      setupListeners () {
        super.setupListeners()
        this.onCloseClick = this.onCloseClick.bind(this)

        this.listeners.add(
          this.els.select.element,
          'change',
          this.updateLanguages.bind(this),
        )

        this.listeners.add(
          this.els.submit.element,
          'click',
          this.handleSubmit.bind(this),
        )

        this.listeners.add(
          this.els.resolve.element,
          'click',
          this.resolveShippingWarning.bind(this),
        )

        this.listeners.add(
          this.els.back.element,
          'click',
          this.hideShippingWarning.bind(this),
        )
      }

      showShippingWarning (resolveFnc) {
        this.els.choose.element.setAttribute('aria-hidden', 'true')
        this.els.warn.element.setAttribute('aria-hidden', 'false')
        this.resolveFnc = resolveFnc
      }

      hideShippingWarning () {
        this.els.choose.element.setAttribute('aria-hidden', 'false')
        this.els.warn.element.setAttribute('aria-hidden', 'true')
        this.resolveFnc = null
      }

      resolveShippingWarning (continueFnc) {
        this.state.shippingConflictResolved = true

        if (this.resolveFnc) {
          const resolve = this.resolveFnc.bind(this)
          const event = new Event('resolveShippingWarning', {})
          resolve(event)
        }
      }

      // We need to know if the user is using the select element
      // or simply accepting the store they are on
      hasShippingConflict (type = 'accept') {
        let conflict = false

        if (this.state.shippingConflictResolved) {
          return conflict
        }

        const selectElement = this.els.select.element
        let { host } = new URL(selectElement.value)

        if (type === 'accept') {
          if (this.state.storefront === 'US') {
            host = this.usHost
          } else {
            host = ''
          }
        }

        if (host === this.usHost) {
          conflict = !this.state.usCountries.includes(
            this.state.customerCountryCode,
          )
        } else {
          conflict = this.state.usCountries.includes(
            this.state.customerCountryCode,
          )
        }

        return conflict
      }

      handleDeRedirect (url) {
        const redirectURL = new URL(url)
        const redirectHost = redirectURL.hostname
        if (redirectHost === this.euHost) {
          redirectURL.hostname = this.deHost
          return redirectURL.toString()
        }

        return url
      }

      handleSubmit (e) {
        try {
          const shippingConflict = this.hasShippingConflict('submit')
          if (shippingConflict) {
            this.showShippingWarning(this.handleSubmit)
            return
          }

          const selectElement = this.els.select.element
          let redirect = selectElement.value

          if (this.state.deRedirect) {
            redirect = this.handleDeRedirect(redirect)
          }

          redirect = this.applyLanguage(redirect)

          if (redirect) {
            const redirectUrl = new URL(redirect)

            new URLSearchParams(window.location.search).forEach(
              (value, key) => {
                if (!redirectUrl.searchParams.has(key)) {
                  redirectUrl.searchParams.set(key, value)
                }
              },
            )

            redirectUrl.searchParams.set(
              'redirect_origin',
              this.state.storefront,
            )

            window.localStorage.setItem(this.storageName, 'true')
            this.showModal = false

            this.pushStoreSelectorEvent('cross_domain_redirect', {
              event_context: 'store-selector-redirect',
              target_url: redirectUrl.href,
              target_value:
                selectElement.selectedOptions[0]?.dataset.countryCode,
            })

            window.location.href = redirectUrl.href
          }
        } catch (err) {
          console.error(err.message)
        }
      }

      onCloseClick (event) {
        event.preventDefault()
        const shippingConflict = this.hasShippingConflict('accept')
        if (shippingConflict) {
          this.showShippingWarning(this.onCloseClick)
          return
        }

        window.localStorage.setItem(this.storageName, 'true')
        this.showModal = false
      }

      set showModal (value) {
        this.state.showModal = value
        this.updateView()
      }

      get showModal () {
        return this.state.showModal
      }

      get forceDisplay () {
        if (this.dataset.forceDisplay !== 'true') {
          return false
        }

        return (
          window.theme.isStagingTheme === true ||
          window.theme.isDevTheme === true
        )
      }

      get storefront () {
        const hostname = window.location.hostname

        if (hostname === 'eu.ohpolly.com') {
          return 'EU'
        }

        if (hostname === 'de.ohpolly.com') {
          return 'DE'
        }

        if (window.Shopify.shop === 'oh-polly-development.myshopify.com') {
          return 'DEV'
        }

        if (window.Shopify.shop === 'oh-polly-uk.myshopify.com') {
          return 'UK'
        }

        if (window.Shopify.shop === 'oh-polly-us.myshopify.com') {
          return 'US'
        }

        if (window.Shopify.shop === 'oh-polly-au.myshopify.com') {
          return 'AU'
        }

        if (window.Shopify.shop === 'oh-polly-de.myshopify.com') {
          return 'DE'
        }

        if (window.Shopify.shop === 'oh-polly-ae.myshopify.com') {
          return 'AE'
        }
      }

      get usHost () {
        return 'us.ohpolly.com'
      }

      get euHost () {
        return 'eu.ohpolly.com'
      }

      get deHost () {
        return 'de.ohpolly.com'
      }

      get storageName () {
        return 'StoreRedirectModalClosed'
      }

      get marketConflictTrackedKey () {
        return 'MarketPreferenceConflictTracked'
      }

      get userCurrencyName () {
        return 'BAO-currency-polly'
      }
    },
  )
}
